import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileText, PenLine, ScrollText, CheckCircle2, AlertTriangle, Loader2, Plus, Download, ShieldCheck, History } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LegalSignoffDialog from "@/components/deals/LegalSignoffDialog";
import ReviewerPickerDialog from "@/components/deals/ReviewerPickerDialog";

const STAFF_ROLES = ["deal_manager", "admin", "super_admin", "compliance_director", "senior_verification_manager", "compliance_reviewer"];
const LEGAL_SIGNOFF_ROLES = ["compliance_director", "super_admin", "senior_verification_manager"];
const SIGNOFF_ELIGIBLE = ["draft", "generated", "legal_review_required", "sent_for_review", "changes_requested"];
const OPEN_TO_SIGN = ["approved_for_signature", "signing_in_progress", "partially_signed"];

function statusColor(s) {
  if (["effective", "fully_signed"].includes(s)) return "bg-emerald-100 text-emerald-700";
  if (["canceled", "terminated", "disputed", "expired"].includes(s)) return "bg-red-100 text-red-700";
  if (["partially_signed", "signing_in_progress", "approved_for_signature", "legal_reviewed"].includes(s)) return "bg-blue-100 text-blue-700";
  return "bg-slate-100 text-slate-600";
}

export default function ContractsPanel({ deal }) {
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user?.role);
  const isLegal = LEGAL_SIGNOFF_ROLES.includes(user?.role);
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [tmplKey, setTmplKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [signoffFor, setSignoffFor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [compareFor, setCompareFor] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [timelineFor, setTimelineFor] = useState(null);
  const [timelineData, setTimelineData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/contract-templates"),
        api.get(`/deals/${deal.deal_id}/contracts`),
      ]);
      const tm = t.data.templates || [];
      const byKey = {};
      tm.forEach((x) => { if (!byKey[x.key] || (x.version || 0) > (byKey[x.key].version || 0)) byKey[x.key] = x; });
      const deduped = Object.values(byKey);
      setTemplates(deduped);
      if (!tmplKey && deduped.length) setTmplKey(deduped[0].key);
      setContracts(c.data.contracts || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, [deal.deal_id, tmplKey]);

  useEffect(() => { load(); }, [deal.deal_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(""); }
  };

  const generate = () => act("gen", () => api.post(`/deals/${deal.deal_id}/contracts/generate`, { template_key: tmplKey }), "Contract generated (DRAFT)");

  const transition = (c, to) => act(`tx-${c.contract_id}`, () => api.post(`/contracts/${c.contract_id}/transition`, { to_status: to }), `Moved to ${to.replace(/_/g, " ")}`);

  const sign = async (c) => {
    const signer_title = window.prompt("Enter your title (e.g. CEO) to sign:");
    if (!signer_title) return;
    let password = null;
    if ((c.fields?.total_value || 0) >= 100000) {
      password = window.prompt("High-value contract — re-enter your password to sign:");
      if (!password) return;
    }
    act(`sign-${c.contract_id}`, () => api.post(`/contracts/${c.contract_id}/sign`, { signer_title, authority_attestation: true, password }), "Signature recorded");
  };

  const checkConsistency = (c) => act(`cons-${c.contract_id}`, async () => {
    const { data } = await api.get(`/contracts/${c.contract_id}/consistency`);
    toast[data.consistent ? "success" : "warning"](data.consistent ? "Contract matches the deal terms" : `Mismatches: ${data.mismatches.map((m) => m.field).join(", ")}`);
  });

  const amend = (c) => act(`amend-${c.contract_id}`, () => api.post(`/contracts/${c.contract_id}/amend`, { reason: "terms updated" }), "Amendment created — re-signature required");

  const amendToCurrent = (c) => act(`amendcur-${c.contract_id}`,
    () => api.post(`/contracts/${c.contract_id}/amend`, { reason: "amend to current deal terms" }),
    "New DRAFT version created from current deal terms — fresh legal review required");

  const resolveDeposit = (c) => {
    const note = window.prompt("Resolve deposit discrepancy — record a reason/authorization note:", "");
    if (note === null) return;
    if (!note.trim()) { toast.error("A resolution note is required"); return; }
    act(`dep-${c.contract_id}`, () => api.post(`/contracts/${c.contract_id}/resolve-deposit-check`, { note }),
      "Deposit discrepancy resolved by reviewer");
  };


  const downloadCertificate = (c) => act(`cert-${c.contract_id}`, async () => {
    const res = await api.get(`/contracts/${c.contract_id}/certificate-pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `CERTIFICATE_${c.contract_id}_v${c.version}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  }, "Simulated Signature Certificate downloaded (NON-BINDING)");

  const openCompare = async (c) => {
    setCompareFor(c.contract_id); setCompareData(null);
    try {
      const { data } = await api.get(`/contracts/${c.contract_id}/snapshot-compare`);
      setCompareData({ ...data, mode: "snapshot" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setCompareFor(null); }
  };

  const openVersionDiff = async (c) => {
    setCompareFor(c.contract_id); setCompareData(null);
    try {
      const { data } = await api.get(`/contracts/${c.contract_id}/version-diff`);
      setCompareData({ ...data, mode: "version" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setCompareFor(null); }
  };

  const openTimeline = async (c) => {
    if (timelineFor === c.contract_id) { setTimelineFor(null); setTimelineData(null); return; }
    setTimelineFor(c.contract_id); setTimelineData(null);
    try {
      const { data } = await api.get(`/contracts/${c.contract_id}/timeline`);
      setTimelineData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setTimelineFor(null); }
  };

  const downloadPdf = (c) => act(`pdf-${c.contract_id}`, async () => {
    const res = await api.get(`/contracts/${c.contract_id}/draft-pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `DRAFT_${c.contract_id}_v${c.version}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  }, "DRAFT PDF downloaded (NOT executable — review only)");

  const downloadTimelinePdf = (c) => act(`tlpdf-${c.contract_id}`, async () => {
    const res = await api.get(`/contracts/${c.contract_id}/timeline-pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `TIMELINE_${c.contract_id}_v${c.version}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  }, "Audit timeline PDF downloaded (read-only reference)");

  const iAmSigner = (c) => (c.signers || []).some((s) => s.user_id === user?.user_id);

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="contracts-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-600"><ScrollText size={15} /> Contracts</h4>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 px-2 py-0.5 rounded">DRAFT — legal review required</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="contract-template-select" value={tmplKey} onChange={(e) => setTmplKey(e.target.value)}
          className="h-9 px-3 rounded-md border border-slate-300 text-sm">
          {templates.map((t) => <option key={t.key} value={t.key}>{t.title}</option>)}
        </select>
        <button data-testid="generate-contract-btn" disabled={busy === "gen" || !tmplKey} onClick={generate}
          className="h-9 px-3 rounded-md bg-[#2563EB] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">
          {busy === "gen" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Generate
        </button>
      </div>

      {loading ? <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="space-y-3" data-testid="contracts-list">
          {contracts.length === 0 && <p className="text-sm text-slate-400">No contracts yet. Generate the Purchase Agreement to enable funding.</p>}
          {contracts.map((c) => (
            <div key={c.contract_id} data-testid={`contract-${c.contract_id}`} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <FileText size={15} className="text-slate-400 shrink-0" />
                    <span className="font-medium text-[#0A0F1C] text-sm truncate">{c.title} <span className="text-slate-400">v{c.version}</span></span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-400 font-mono truncate">hash {c.document_hash?.slice(0, 16)}…</div>
                  {c.prefill_context && (
                    <span data-testid={`prefill-badge-${c.contract_id}`}
                      className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                      <CheckCircle2 size={10} /> Prefilled from pre-deal negotiation
                    </span>
                  )}
                  {c.legal_signoff && (
                    <span data-testid={`legal-reviewed-badge-${c.contract_id}`}
                      className="mt-1 ml-1 inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700"
                      title={`By ${c.legal_signoff.authority_title} (${c.legal_signoff.reviewer_role}) on ${c.legal_signoff.signed_off_at} · v${c.legal_signoff.contract_version}`}>
                      <ShieldCheck size={10} /> Legally reviewed · {c.legal_signoff.authority_title}
                    </span>
                  )}
                  {c.assigned_reviewer && (
                    <span data-testid={`assigned-reviewer-badge-${c.contract_id}`}
                      className="mt-1 ml-1 inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600"
                      title={`Assigned by ${c.assigned_reviewer.assigned_by_role} on ${c.assigned_reviewer.assigned_at}`}>
                      Reviewer: {c.assigned_reviewer.name || c.assigned_reviewer.email}
                    </span>
                  )}
                </div>
                <span data-testid={`contract-status-${c.contract_id}`} className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${statusColor(c.status)}`}>{c.status?.replace(/_/g, " ")}</span>
              </div>

              {c.terms_review_required && (
                <div data-testid={`terms-guard-${c.contract_id}`} className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <div>
                    <strong>RE-REVIEW REQUIRED</strong> — deal terms changed after legal review. The prior approval was invalidated; a fresh legal sign-off is required.
                    <div className="mt-0.5 text-red-600">Changed: {(c.changed_terms || []).map((d) => d.field.replace(/_/g, " ")).join(", ")}</div>
                  </div>
                </div>
              )}

              {c.deposit_check?.applicable && !c.deposit_check.consistent && (
                <div data-testid={`deposit-check-${c.contract_id}`} className="mt-2 flex items-start justify-between gap-2 rounded-md border border-orange-200 bg-orange-50 p-2 text-[11px] text-orange-800">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    <div>
                      <strong>DEPOSIT MISMATCH</strong> — entered {c.deposit_check.entered} vs expected {c.deposit_check.expected} ({c.deposit_check.deposit_pct}% of {c.deposit_check.total_value}). Approval is blocked until corrected or resolved.
                      {c.deposit_check_resolution && <div className="mt-0.5 text-emerald-700">Resolved by reviewer: {c.deposit_check_resolution.note}</div>}
                    </div>
                  </div>
                  {isLegal && !c.deposit_check_resolution && (
                    <button data-testid={`resolve-deposit-${c.contract_id}`} disabled={busy === `dep-${c.contract_id}`} onClick={() => resolveDeposit(c)}
                      className="h-6 shrink-0 px-2 rounded bg-orange-600 text-white text-[10px] font-semibold">Resolve</button>
                  )}
                </div>
              )}

              {compareFor === c.contract_id && (
                <div data-testid={`snapshot-compare-${c.contract_id}`} className="mt-2 rounded-md border border-slate-200 bg-white p-2 text-[11px]">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-semibold text-slate-700">
                      {compareData?.mode === "version"
                        ? `Previous version (v${compareData?.previous_version}) vs current (v${compareData?.current_version})`
                        : `Effective snapshot (v${compareData?.snapshot_version}) vs current deal terms`}
                    </span>
                    <button className="text-slate-400 hover:text-slate-600" onClick={() => { setCompareFor(null); setCompareData(null); }}>close</button>
                  </div>
                  {!compareData && <span className="text-slate-400">Loading…</span>}
                  {compareData && compareData.differences?.length === 0 && (
                    <span className="text-emerald-700">No differences.</span>
                  )}
                  {compareData && compareData.differences?.length > 0 && (
                    <>
                      {compareData.mode === "snapshot" && compareData.last_change && <div className="mb-1 text-slate-500">Last live change by {compareData.last_change.by || "—"} at {compareData.last_change.at || "—"}</div>}
                      {compareData.mode === "version" && compareData.amended && <div className="mb-1 text-slate-500">Amended by {compareData.amended.by || "—"} at {compareData.amended.at || "—"}{compareData.amended.reason ? ` · ${compareData.amended.reason}` : ""}</div>}
                      <table className="w-full">
                        <thead><tr className="text-slate-400 text-left"><th className="py-0.5">Field</th><th>{compareData.mode === "version" ? "Previous" : "Snapshot (agreed)"}</th><th>Current</th><th></th></tr></thead>
                        <tbody>
                          {compareData.differences.map((d) => (
                            <tr key={d.field} className="border-t border-slate-100">
                              <td className="py-0.5 font-medium text-slate-700">{d.field.replace(/_/g, " ")}</td>
                              <td className="text-slate-500">{String((compareData.mode === "version" ? d.previous : d.snapshot) ?? "—")}</td>
                              <td className="text-red-600 font-medium">{String(d.current ?? "—")}</td>
                              <td><span className="rounded bg-amber-100 px-1 text-[9px] font-semibold uppercase text-amber-700">{d.change_type}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-1 text-[10px] text-slate-400">Read-only — prior versions/snapshot are preserved unchanged; material differences feed the Deal Terms Guard / re-review.</div>
                    </>
                  )}
                </div>
              )}
              {timelineFor === c.contract_id && (
                <div data-testid={`timeline-${c.contract_id}`} className="mt-2 rounded-md border border-slate-200 bg-white p-3 text-[11px]">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 font-semibold text-slate-700"><History size={12} /> Contract timeline</span>
                    <div className="flex items-center gap-2">
                      <button data-testid={`timeline-pdf-${c.contract_id}`} disabled={busy === `tlpdf-${c.contract_id}`} onClick={() => downloadTimelinePdf(c)}
                        className="inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                        {busy === `tlpdf-${c.contract_id}` ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />} Timeline PDF
                      </button>
                      <button className="text-slate-400 hover:text-slate-600" onClick={() => { setTimelineFor(null); setTimelineData(null); }}>close</button>
                    </div>
                  </div>
                  {!timelineData && <span className="text-slate-400">Loading…</span>}
                  {timelineData && timelineData.timeline?.length === 0 && <span className="text-slate-400">No recorded events yet.</span>}
                  {timelineData && timelineData.timeline?.length > 0 && (
                    <ol className="relative ml-1 border-l border-slate-200">
                      {timelineData.timeline.map((ev, i) => (
                        <li key={i} data-testid={`timeline-entry-${c.contract_id}-${i}`} className="ml-3 pb-3 last:pb-0">
                          <span className="absolute -left-[5px] mt-1 h-2 w-2 rounded-full bg-[#2563EB]" />
                          <div className="font-medium text-slate-800">{ev.label}{ev.version ? <span className="text-slate-400"> · v{ev.version}</span> : null}</div>
                          <div className="text-slate-500">
                            {ev.at ? new Date(ev.at).toLocaleString() : "—"}
                            {ev.actor_name ? <> · <span className="text-slate-600">{ev.actor_name}</span></> : null}
                            {ev.actor_role ? <span className="text-slate-400"> ({ev.actor_role.replace(/_/g, " ")})</span> : null}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                  <div className="mt-1 text-[10px] text-slate-400">Read-only — assembled from the append-only contract audit; not a separate log.</div>
                </div>
              )}
              {(c.signatures?.length > 0 || OPEN_TO_SIGN.includes(c.status)) && (
                <div className="mt-2" data-testid={`sign-progress-${c.contract_id}`}>
                  <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700">
                    <PenLine size={11} /> Signatures — SIMULATED / NON-BINDING
                    <span className="font-normal normal-case text-slate-400">
                      ({(c.signatures || []).length}/{(c.signers || []).filter((x) => x.required).length} required signed)
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(c.signers || []).map((sg) => {
                      const done = (c.signatures || []).find((s) => s.signer_user_id === sg.user_id);
                      return (
                        <span key={sg.user_id} className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${done ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {done ? <CheckCircle2 size={11} /> : <PenLine size={11} />} {sg.party}{done ? ` · ${done.signer_title}` : " · awaiting"}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-1.5">
                <button data-testid={`view-clauses-${c.contract_id}`} onClick={() => setExpanded(expanded === c.contract_id ? null : c.contract_id)}
                  className="h-7 px-2 rounded border border-slate-300 text-xs text-slate-600">View clauses</button>
                <button data-testid={`view-timeline-${c.contract_id}`} onClick={() => openTimeline(c)}
                  className="h-7 px-2 rounded border border-slate-300 text-xs text-slate-600 inline-flex items-center gap-1">
                  <History size={11} /> {timelineFor === c.contract_id ? "Hide timeline" : "View timeline"}
                </button>
                <button data-testid={`consistency-${c.contract_id}`} disabled={busy === `cons-${c.contract_id}`} onClick={() => checkConsistency(c)}
                  className="h-7 px-2 rounded border border-slate-300 text-xs text-slate-600">Consistency check</button>
                <button data-testid={`download-pdf-${c.contract_id}`} disabled={busy === `pdf-${c.contract_id}`} onClick={() => downloadPdf(c)}
                  className="h-7 px-2 rounded border border-slate-300 text-xs text-slate-600 inline-flex items-center gap-1">
                  {busy === `pdf-${c.contract_id}` ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} DRAFT PDF
                </button>
                {c.amended_from && (
                  <button data-testid={`version-diff-${c.contract_id}`} onClick={() => openVersionDiff(c)}
                    className="h-7 px-2 rounded border border-slate-300 text-xs text-slate-600">Compare with previous version</button>
                )}
                {c.status === "effective" && c.effective_snapshot && (
                  <>
                    <button data-testid={`download-cert-${c.contract_id}`} disabled={busy === `cert-${c.contract_id}`} onClick={() => downloadCertificate(c)}
                      className="h-7 px-2 rounded bg-slate-800 text-white text-xs inline-flex items-center gap-1">
                      {busy === `cert-${c.contract_id}` ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Download Simulated Signature Certificate
                    </button>
                    <button data-testid={`compare-snapshot-${c.contract_id}`} onClick={() => openCompare(c)}
                      className="h-7 px-2 rounded border border-slate-300 text-xs text-slate-600">Compare snapshot vs current</button>
                  </>
                )}
                {isLegal && SIGNOFF_ELIGIBLE.includes(c.status) && (
                  <button data-testid={`legal-signoff-${c.contract_id}`} onClick={() => setSignoffFor(c)}
                    className="h-7 px-2 rounded bg-emerald-600 text-white text-xs">Record legal sign-off</button>
                )}
                {isLegal && (
                  <button data-testid={`assign-reviewer-${c.contract_id}`} onClick={() => setAssignFor(c)}
                    className="h-7 px-2 rounded border border-slate-300 text-xs text-slate-600">
                    {c.assigned_reviewer ? "Reassign reviewer" : "Assign reviewer"}
                  </button>
                )}
                {isStaff && c.status === "legal_reviewed" && (
                  <button data-testid={`approve-sign-${c.contract_id}`} disabled={busy === `tx-${c.contract_id}`} onClick={() => transition(c, "approved_for_signature")}
                    className="h-7 px-2 rounded bg-slate-800 text-white text-xs">Approve for signature</button>
                )}
                {iAmSigner(c) && OPEN_TO_SIGN.includes(c.status) && (
                  <button data-testid={`sign-${c.contract_id}`} disabled={busy === `sign-${c.contract_id}`} onClick={() => sign(c)}
                    className="h-7 px-2 rounded bg-[#C2410C] text-white text-xs flex items-center gap-1"><PenLine size={12} /> Sign</button>
                )}
                {c.terms_review_required && (
                  <button data-testid={`amend-to-current-${c.contract_id}`} disabled={busy === `amendcur-${c.contract_id}`} onClick={() => amendToCurrent(c)}
                    className="h-7 px-2 rounded bg-amber-600 text-white text-xs inline-flex items-center gap-1">
                    {busy === `amendcur-${c.contract_id}` ? <Loader2 size={11} className="animate-spin" /> : <ScrollText size={11} />} Amend to Current Deal Terms
                  </button>
                )}
                {c.status === "effective" && (
                  <button data-testid={`amend-${c.contract_id}`} disabled={busy === `amend-${c.contract_id}`} onClick={() => amend(c)}
                    className="h-7 px-2 rounded border border-slate-300 text-xs text-slate-600">Amend</button>
                )}
              </div>

              {expanded === c.contract_id && (
                <div className="mt-3 rounded bg-slate-50 p-3 text-xs text-slate-600 whitespace-pre-wrap" data-testid={`clauses-${c.contract_id}`}>
                  <div className="flex items-center gap-1 text-amber-700 font-semibold mb-1"><AlertTriangle size={12} /> {c.label}</div>
                  {renderBody(c)}
                  {c.prefill_context && <PrefillProvenance ctx={c.prefill_context} cid={c.contract_id} />}
                  <SignoffTrail c={c} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {signoffFor && (
        <LegalSignoffDialog contract={signoffFor} open={!!signoffFor}
          onOpenChange={(v) => !v && setSignoffFor(null)} onDone={load} />
      )}
      {assignFor && (
        <ReviewerPickerDialog contract={assignFor} open={!!assignFor}
          onOpenChange={(v) => !v && setAssignFor(null)} onDone={load} />
      )}
    </div>
  );
}

function renderBody(c) {
  const tmplLine = Object.entries(c.fields || {})
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v ?? "—"}`)
    .join("\n");
  return `Snapshotted deal terms:\n${tmplLine}`;
}

function SignoffTrail({ c }) {
  const history = c.legal_signoff_history || [];
  const guardEvents = c.terms_guard_events || [];
  if (history.length === 0 && guardEvents.length === 0) return null;
  return (
    <div className="mt-3 border-t border-slate-200 pt-2 not-italic" data-testid={`signoff-trail-${c.contract_id}`}>
      <div className="mb-1 font-semibold text-blue-700">Legal / compliance sign-off trail (append-only)</div>
      {history.length === 0 && <div className="text-slate-400">No legal sign-off recorded yet.</div>}
      {history.map((s, i) => {
        const superseded = c.legal_signoff && c.legal_signoff.signed_off_at === s.signed_off_at;
        return (
          <div key={i} data-testid={`signoff-entry-${c.contract_id}-${i}`}
            className="mb-1.5 rounded border border-slate-200 bg-white p-2">
            <div className="flex flex-wrap items-center justify-between gap-1">
              <span className="font-medium text-slate-800">{s.authority_title} <span className="text-slate-400">· {s.reviewer_role}</span></span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${superseded ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {superseded ? "ACTIVE" : "SUPERSEDED"}
              </span>
            </div>
            <div className="text-[11px] text-slate-500">Decision: legally reviewed · Contract v{s.contract_version} · {s.signed_off_at ? new Date(s.signed_off_at).toLocaleString() : ""}</div>
            {s.checklist?.length > 0 && (
              <div className="mt-0.5 text-[11px] text-slate-600">
                {s.checklist.map((ci) => `${ci.checked ? "✓" : "✗"} ${ci.item.replace(/_/g, " ")}`).join("  ·  ")}
              </div>
            )}
            {s.evidence && <div className="text-[11px] text-slate-600">Evidence: {s.evidence}</div>}
            {s.notes && <div className="text-[11px] text-slate-600">Notes: {s.notes}</div>}
            <div className="text-[10px] text-slate-400 font-mono">reviewer {String(s.reviewer_user_id).slice(0, 10)}… · hash {String(s.document_hash).slice(0, 12)}…</div>
          </div>
        );
      })}
      {guardEvents.length > 0 && (
        <div className="mt-1">
          <div className="font-semibold text-red-700">Deal Terms Guard events</div>
          {guardEvents.map((g, i) => (
            <div key={i} data-testid={`guard-event-${c.contract_id}-${i}`} className="text-[11px] text-red-600">
              {g.detected_at ? new Date(g.detected_at).toLocaleString() : ""} — terms changed ({(g.changed_terms || []).map((d) => d.field.replace(/_/g, " ")).join(", ")}); prior approval (v{g.invalidated_signoff_version ?? "—"}) invalidated from {g.previous_status?.replace(/_/g, " ")}.
            </div>
          ))}
        </div>
      )}
      <div className="mt-1 text-[10px] text-slate-400">Append-only audit trail — prior approvals are never edited or deleted.</div>
    </div>
  );
}

function PrefillProvenance({ ctx, cid }) {
  const q = ctx.accepted_quote;
  const cap = ctx.capacity_confirmation;
  const need = ctx.need || {};
  return (
    <div className="mt-3 border-t border-slate-200 pt-2 not-italic" data-testid={`prefill-provenance-${cid}`}>
      <div className="mb-1 font-semibold text-emerald-700">Prefilled from pre-deal negotiation (source of the draft terms)</div>
      {(need.title || need.product_service) && (
        <div>Source Need: <strong>{need.title || need.product_service}</strong>{need.geography ? ` · ${need.geography}` : ""}{need.quantity ? ` · ${need.quantity}` : ""}</div>
      )}
      {need.transaction_requirements && <div>Requirements/specs: {need.transaction_requirements}</div>}
      {q && (
        <div>Accepted quote: <strong>{q.quantity} {q.unit} @ {q.currency || "USD"} {q.unit_price}</strong>{q.incoterm ? ` (${q.incoterm})` : ""}{q.delivery_date ? ` · delivery ${q.delivery_date}` : ""}</div>
      )}
      {cap ? (
        <div>Business-confirmed capacity: <strong>{cap.quantity} {cap.unit}</strong>{cap.availability_timeframe ? ` · ${cap.availability_timeframe}` : ""}{cap.confirming_authority ? ` · by ${cap.confirming_authority}` : ""}</div>
      ) : (
        <div className="text-amber-700">Capacity was not business-confirmed before this draft.</div>
      )}
      <div className="mt-1 text-[10px] text-slate-400">Provenance only — legal clauses are unchanged and require legal review. Material changes create a new version/amendment.</div>
    </div>
  );
}
